import * as React from 'react';
import type { PiletApi } from '@chess/platform-web';
import { create } from 'zustand';
import { type Phase } from '@chess/api-model';
import { OPENINGS_API, type Openings } from '@chess/api-openings';
import { PHASES_API, REVIEW_API, phaseRange, type Review, type ReviewState } from '@chess/api-review';
import { CONSTRAINTS_API, type ConstraintRegistry } from '@chess/api-constraints';
import { useConstraint, useConstraintRegistry } from '@chess/api-constraints/react';
import { isHidden, tabsHidden } from './hidden.ts';
import { stageToOpen, type Followed } from './follow-stage.ts';
import { phasesProvider } from './provider.ts';
import { nodeOf, type Node } from './node.ts';
import { SLOT, TABS, byOrder, controlsHeld, isPhase, tabClick } from './tab-click.ts';
import { INNER_TABS, groupByTab, pickActive, tabsPresent, type InnerTab } from './inner-tabs.ts';
import type { TabBadgeEvent } from './events.d.ts';
import { StageBody } from './StageBody.tsx';
import './phases.css';

// The review's three stage tabs, Opening, Middlegame and Ending, and the
// `phases` provider that decides where the stages are: the opening ends with
// the move that left the book, that move included; the endgame starts where
// the material thins; a game the book never knew leaves the opening at move
// 12. The review's loader asks this provider when a game opens and keeps the
// answer with the game, so every tab, the puzzles and the history agree.
//
// Each tab owns no content: it renders the generic stage header
// (`review.stageHeader`, from plugin-review-stage-header), then a small
// inner strip, Overview and Puzzles, and the active inner tab's
// contributions from the stage's sections slot, `review.<phase>.sections`.
// A contribution's registration defaults say which inner tab it is on
// (`tab: 'puzzles'`; overview otherwise). The active inner tab is this
// plugin's own state, remembered per stage. While a puzzle hides the moves
// (the board's Scoresheet visibility constraint) the panel is hidden, so its
// rows do not give the answer away, and the stage tabs above it are hidden
// with it through `layout.tabsHidden` (the strip is the layout's), the
// selected tab unchanged (hidden.ts).

const sectionsOf = (phase: Phase) => `review.${phase}.sections`;

/** What every section under a stage tab is handed. */
export interface SectionParams {
  gameId: string;
  phase: Phase;
  /** The stage's first and last positions; its moves are `fromPly + 1 … toPly`. */
  fromPly: number;
  toPly: number;
  /** The opening tab only: the named position the sections are about, the board's own name when it has one, else the game's deepest named node. */
  node?: Node;
}

interface TabState {
  review: ReviewState;
  node?: Node;
  /** The inner tab each stage is on; overview until the reader picks. */
  inner: Partial<Record<Phase, InnerTab>>;
  /** Each stage's puzzle count, from the puzzles plugin's badge event. */
  badges: Partial<Record<Phase, string | number | undefined>>;
  pick(phase: Phase, tab: InnerTab): void;
}

const useTabs = create<TabState>((set) => ({
  review: { status: 'idle', ply: 0 },
  inner: {},
  badges: {},
  pick: (phase, tab) => set((s) => ({ inner: { ...s.inner, [phase]: tab } })),
}));

export function setup(app: PiletApi) {
  // The provider: the book says where the opening ended, the material where the endgame began. See provider.ts.
  app.registerApi(PHASES_API, phasesProvider(app.getApi<Openings>(OPENINGS_API)));

  // A stage's puzzle count, shown on its inner Puzzles tab when a stage has one (none does today).
  app.on('review.tabBadge', (event: TabBadgeEvent) => {
    if (event.slot !== SLOT || !isPhase(event.id)) return;
    useTabs.setState((s) => ({ badges: { ...s.badges, [event.id]: event.badge } }));
  });

  // Following the game: when the position on the board moves into another
  // stage of the actual game (the move controls, the scoresheet, opening a
  // game at a ply), that stage's tab is opened. Only on a change of stage, so
  // a tab picked by hand stays until the game reaches another stage. A line
  // the reader is exploring is not the game and never opens a stage, and
  // neither does stepping back while the board's Controls constraint holds
  // it, as a puzzle does. See follow-stage.ts.
  let registry: ConstraintRegistry | undefined;
  void app.getApi<ConstraintRegistry>(CONSTRAINTS_API).then((r) => {
    registry = r;
    followHidden(lastState);
  });

  // The stage tabs' strip is hidden with the panel: this follows the active
  // board's Scoresheet visibility constraint (one subscription per scope,
  // renewed when the board tab changes) and emits the layout event (hidden.ts).
  let lastState: ReviewState | undefined;
  let hiddenScope: string | undefined;
  let offHidden: (() => void) | undefined;
  const followHidden = (state: ReviewState | undefined) => {
    const scope = state?.scope;
    const key = scope ? `${scope.kind}:${scope.id}` : undefined;
    if (key === hiddenScope && (offHidden || !registry)) return;
    offHidden?.();
    offHidden = undefined;
    hiddenScope = key;
    if (!registry || !scope) {
      app.emit('layout.tabsHidden', tabsHidden(SLOT, {}));
      return;
    }
    offHidden = registry.subscribe(scope, 'scoresheetVisibility', (value) => app.emit('layout.tabsHidden', tabsHidden(SLOT, value)));
    app.emit('layout.tabsHidden', tabsHidden(SLOT, registry.current(scope, 'scoresheetVisibility')));
  };
  const followed: Followed = {};
  const followStage = (state: ReviewState) => {
    const phase = stageToOpen(state, followed, controlsHeld(registry, state));
    if (!phase || !state.game) return;
    followed.gameId = state.game.id;
    followed.phase = phase;
    app.emit('layout.selectTab', { slot: SLOT, id: phase });
  };

  void app.getApi<Review>(REVIEW_API).then((review) => {
    // A click on a stage tab moves the board to the stage's first position,
    // recording the stage it lands in as followed. See tab-click.ts.
    app.on('layout.tabClick', (event) => {
      if (event.slot !== SLOT || !isPhase(event.id)) return;
      const state = review.state();
      const ply = tabClick(state, event.id, controlsHeld(registry, state), followed);
      if (ply !== undefined) review.go(ply);
    });
    const onState = (state: ReviewState) => {
      lastState = state;
      followHidden(state);
      followStage(state);
      // The opening tab's sections are about a named position, read off the
      // review's book (`state.book`): the board's own name when it has one,
      // else the deepest named node of the game. See node.ts.
      useTabs.setState({ review: state, node: nodeOf(state) });
    };
    review.subscribe(onState);
    onState(review.state());
  });

  /** The inner strip and the active tab's contributions, built from the slot's rendered children. */
  const InnerTabs: React.FC<{ phase: Phase; children: React.ReactNode }> = ({ phase, children }) => {
    const wanted = useTabs((s) => s.inner[phase]);
    const badge = useTabs((s) => s.badges[phase]);
    const pick = useTabs((s) => s.pick);
    const elements = React.Children.toArray(children) as React.ReactElement[];
    // A rendered contribution carries its registration defaults merged into `params`.
    const groups = groupByTab(elements, (el) => el.props?.params as { tab?: unknown } | undefined);
    const present = tabsPresent(groups);
    const active = pickActive(wanted, present);
    // Pinned contributions sit right under the stage header, above the strip.
    return (
      <StageBody
        pinned={groups.header.length > 0 ? groups.header : undefined}
        present={present}
        active={active}
        badge={badge}
        onPick={(tab) => pick(phase, tab)}>
        {active ? groups[active] : null}
      </StageBody>
    );
  };

  const tabFor = (phase: Phase, title: string): React.FC => () => {
    const { review, node } = useTabs();
    const constraints = useConstraintRegistry(app);
    const hidden = isHidden(useConstraint(constraints, review.scope, 'scoresheetVisibility'));
    const game = review.game;
    const params: SectionParams | undefined =
      game && review.gameId
        ? { gameId: review.gameId, phase, ...phaseRange(game.phases, game.moves.length, phase, game.sides?.reader), node: phase === 'opening' ? node : undefined }
        : undefined;
    return (
      <section
        className={`panel review-phase review-phase-${phase} panel-grow${hidden ? ' review-phase-hidden' : ''}`}
        hidden={hidden}>
        <app.Extension name="review.stageHeader" params={{ phase }} empty={() => <h2>{title}</h2>} />
        {params && (
          <app.Extension
            name={sectionsOf(phase)}
            params={params}
            order={byOrder}
            render={(children: React.ReactNode) => <InnerTabs phase={phase}>{children}</InnerTabs>}
          />
        )}
      </section>
    );
  };

  for (const tab of TABS) {
    app.registerExtension(SLOT, tabFor(tab.phase, tab.title), { id: tab.phase, title: tab.title, order: tab.order });
  }
}
